import React, { useState, useEffect, useRef } from 'react';
import { 
  Video, Plus, Calendar, Clock, Users, Link as LinkIcon, Copy, 
  ExternalLink, Trash2, Play, Settings, Monitor, Mic, MicOff,
  VideoOff, Phone, PhoneOff, Share2, MessageSquare
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger 
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import api from '@/lib/api';

const BRONZE = '#C9A961';

// Generate unique meeting ID
const generateMeetingId = () => {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let result = 'wdl-';
  for (let i = 0; i < 3; i++) {
    for (let j = 0; j < 4; j++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    if (i < 2) result += '-';
  }
  return result;
};

// Format date
const formatDate = (dateStr) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { 
    weekday: 'short', 
    month: 'short', 
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Meeting Card Component
const MeetingCard = ({ meeting, onJoin, onDelete, onCopyLink }) => {
  const isUpcoming = new Date(meeting.scheduled_time) > new Date();
  const isLive = meeting.status === 'live';
  
  return (
    <Card className="bg-black/50 border-[#C9A961]/20 hover:border-[#C9A961]/50 transition-all">
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <h3 className="font-semibold text-white text-lg">{meeting.title}</h3>
              {isLive && (
                <Badge className="bg-red-500 text-white animate-pulse">LIVE</Badge>
              )}
              {isUpcoming && !isLive && (
                <Badge className="bg-[#C9A961]/20 text-[#C9A961] border border-[#C9A961]/50">
                  Upcoming
                </Badge>
              )}
            </div>
            
            <div className="space-y-1 text-sm text-gray-400">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                <span>{formatDate(meeting.scheduled_time)}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                <span>{meeting.duration || 60} minutes</span>
              </div>
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                <span>{meeting.participants?.length || 0} invited</span>
              </div>
            </div>
            
            {meeting.description && (
              <p className="text-gray-500 text-sm mt-2 line-clamp-2">{meeting.description}</p>
            )}
          </div>
          
          <div className="flex flex-col gap-2 ml-4">
            <Button 
              onClick={() => onJoin(meeting)}
              style={{ backgroundColor: BRONZE }}
              className="text-black"
            >
              <Play className="h-4 w-4 mr-1" />
              {isLive ? 'Join Now' : 'Start'}
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => onCopyLink(meeting)}
              className="border-[#C9A961]/50 text-[#C9A961]"
            >
              <Copy className="h-4 w-4 mr-1" />
              Copy Link
            </Button>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => onDelete(meeting.id)}
              className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Jitsi Meeting Room Component
const JitsiMeetingRoom = ({ meeting, onClose }) => {
  const jitsiContainerRef = useRef(null);
  const [api, setApi] = useState(null);
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    if (!window.JitsiMeetExternalAPI) {
      // Load Jitsi API script
      const script = document.createElement('script');
      script.src = 'https://8x8.vc/vpaas-magic-cookie-ef5ce88c523d41a599c8b1dc5b3ab765/external_api.js';
      script.async = true;
      script.onload = initJitsi;
      document.head.appendChild(script);
    } else {
      initJitsi();
    }

    return () => {
      if (api) {
        api.dispose();
      }
    };
  }, [meeting]);

  const initJitsi = () => {
    if (!jitsiContainerRef.current) return;

    const domain = 'meet.jit.si';
    const roomName = meeting.meeting_id || generateMeetingId();
    
    const options = {
      roomName: roomName,
      parentNode: jitsiContainerRef.current,
      width: '100%',
      height: '100%',
      userInfo: {
        displayName: `${currentUser.first_name || ''} ${currentUser.last_name || ''}`.trim() || currentUser.username,
        email: currentUser.email
      },
      configOverwrite: {
        startWithAudioMuted: false,
        startWithVideoMuted: false,
        enableWelcomePage: false,
        prejoinPageEnabled: false,
        disableDeepLinking: true,
        toolbarButtons: [
          'microphone',
          'camera',
          'desktop',
          'fullscreen',
          'fodeviceselection',
          'hangup',
          'chat',
          'recording',
          'livestreaming',
          'settings',
          'raisehand',
          'videoquality',
          'filmstrip',
          'participants-pane',
          'tileview',
          'select-background',
          'mute-everyone',
          'mute-video-everyone',
          'security'
        ],
        enableNoisyMicDetection: true,
        enableClosePage: false,
      },
      interfaceConfigOverwrite: {
        SHOW_JITSI_WATERMARK: false,
        SHOW_WATERMARK_FOR_GUESTS: false,
        DEFAULT_BACKGROUND: '#000000',
        TOOLBAR_BUTTONS: [
          'microphone',
          'camera',
          'desktop',
          'fullscreen',
          'hangup',
          'chat',
          'recording',
          'settings',
          'raisehand',
          'videoquality',
          'tileview',
          'participants-pane',
        ],
        SETTINGS_SECTIONS: ['devices', 'language', 'moderator'],
        DISABLE_JOIN_LEAVE_NOTIFICATIONS: false,
      }
    };

    try {
      const jitsiApi = new window.JitsiMeetExternalAPI(domain, options);
      setApi(jitsiApi);

      jitsiApi.addEventListener('videoConferenceLeft', () => {
        onClose();
      });

      jitsiApi.addEventListener('readyToClose', () => {
        onClose();
      });
    } catch (error) {
      console.error('Failed to initialize Jitsi:', error);
      toast.error('Failed to start video meeting');
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black">
      <div className="absolute top-4 right-4 z-50">
        <Button 
          onClick={onClose}
          variant="outline"
          className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
        >
          <PhoneOff className="h-4 w-4 mr-2" />
          Leave Meeting
        </Button>
      </div>
      <div ref={jitsiContainerRef} className="w-full h-full" />
    </div>
  );
};

// Main Meetings Page
const MeetingsPage = () => {
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [activeMeeting, setActiveMeeting] = useState(null);
  const [users, setUsers] = useState([]);
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  
  // New meeting form
  const [newMeeting, setNewMeeting] = useState({
    title: '',
    description: '',
    scheduled_time: '',
    duration: 60,
    participants: []
  });

  // Fetch meetings
  useEffect(() => {
    fetchMeetings();
    fetchUsers();
  }, []);

  const fetchMeetings = async () => {
    try {
      const res = await api.get('/meetings');
      setMeetings(res.data);
    } catch (error) {
      console.error('Error fetching meetings:', error);
      setMeetings([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await api.get('/chat/users');
      setUsers(res.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  // Create meeting
  const handleCreateMeeting = async () => {
    if (!newMeeting.title.trim()) {
      toast.error('Please enter a meeting title');
      return;
    }

    try {
      const meetingData = {
        ...newMeeting,
        meeting_id: generateMeetingId(),
        created_by: currentUser.id,
        status: 'scheduled'
      };

      const res = await api.post('/meetings', meetingData);
      setMeetings(prev => [res.data, ...prev]);
      setShowCreateDialog(false);
      setNewMeeting({
        title: '',
        description: '',
        scheduled_time: '',
        duration: 60,
        participants: []
      });
      toast.success('Meeting scheduled successfully!');
    } catch (error) {
      console.error('Error creating meeting:', error);
      toast.error('Failed to create meeting');
    }
  };

  // Start instant meeting
  const handleInstantMeeting = () => {
    const instantMeeting = {
      id: 'instant-' + Date.now(),
      meeting_id: generateMeetingId(),
      title: `${currentUser.first_name || currentUser.username}'s Meeting`,
      scheduled_time: new Date().toISOString(),
      status: 'live',
      created_by: currentUser.id
    };
    setActiveMeeting(instantMeeting);
  };

  // Join meeting
  const handleJoinMeeting = (meeting) => {
    setActiveMeeting(meeting);
  };

  // Delete meeting
  const handleDeleteMeeting = async (meetingId) => {
    try {
      await api.delete(`/meetings/${meetingId}`);
      setMeetings(prev => prev.filter(m => m.id !== meetingId));
      toast.success('Meeting deleted');
    } catch (error) {
      console.error('Error deleting meeting:', error);
      toast.error('Failed to delete meeting');
    }
  };

  // Copy meeting link
  const handleCopyLink = (meeting) => {
    const link = `https://meet.jit.si/${meeting.meeting_id}`;
    navigator.clipboard.writeText(link);
    toast.success('Meeting link copied to clipboard!');
  };

  // Close meeting room
  const handleCloseMeeting = () => {
    setActiveMeeting(null);
  };

  if (activeMeeting) {
    return <JitsiMeetingRoom meeting={activeMeeting} onClose={handleCloseMeeting} />;
  }

  const upcomingMeetings = meetings.filter(m => new Date(m.scheduled_time) > new Date());
  const pastMeetings = meetings.filter(m => new Date(m.scheduled_time) <= new Date());

  return (
    <div className="min-h-screen p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <img src="/williams-logo-transparent.png" alt="Williams Diversified" className="h-16" />
          <div>
            <h1 className="text-4xl font-bold font-serif flex items-center gap-3">
              <Video className="h-10 w-10" style={{ color: BRONZE }} />
              <span className="text-white">Video</span>{' '}
              <span className="italic" style={{ color: BRONZE }}>Meetings</span>
            </h1>
            <p className="text-gray-400 text-lg">Schedule and join video conferences</p>
          </div>
        </div>
        
        <div className="flex gap-3">
          <Button 
            onClick={() => window.open('/meetings', 'VideoMeetings_Main', 'width=1200,height=900,left=100,top=100')}
            variant="outline"
            className="border-[#C9A961] text-[#C9A961]"
            title="Open in new window"
          >
            <Monitor className="h-5 w-5 mr-2" />
            Pop Out
          </Button>
          
          <Button 
            onClick={handleInstantMeeting}
            style={{ backgroundColor: BRONZE }}
            className="text-black"
          >
            <Video className="h-5 w-5 mr-2" />
            Start Instant Meeting
          </Button>
          
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button variant="outline" className="border-[#C9A961] text-[#C9A961]">
                <Plus className="h-5 w-5 mr-2" />
                Schedule Meeting
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-gray-900 border-gray-700 max-w-lg">
              <DialogHeader>
                <DialogTitle style={{ color: BRONZE }}>Schedule a Meeting</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-4">
                <div>
                  <Label className="text-gray-300">Meeting Title</Label>
                  <Input
                    placeholder="Weekly Team Standup"
                    value={newMeeting.title}
                    onChange={(e) => setNewMeeting(prev => ({ ...prev, title: e.target.value }))}
                    className="bg-black/50 border-gray-700"
                  />
                </div>
                
                <div>
                  <Label className="text-gray-300">Description (Optional)</Label>
                  <Input
                    placeholder="Discuss project updates..."
                    value={newMeeting.description}
                    onChange={(e) => setNewMeeting(prev => ({ ...prev, description: e.target.value }))}
                    className="bg-black/50 border-gray-700"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-300">Date & Time</Label>
                    <Input
                      type="datetime-local"
                      value={newMeeting.scheduled_time}
                      onChange={(e) => setNewMeeting(prev => ({ ...prev, scheduled_time: e.target.value }))}
                      className="bg-black/50 border-gray-700"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-300">Duration (minutes)</Label>
                    <Input
                      type="number"
                      value={newMeeting.duration}
                      onChange={(e) => setNewMeeting(prev => ({ ...prev, duration: parseInt(e.target.value) }))}
                      className="bg-black/50 border-gray-700"
                    />
                  </div>
                </div>
                
                <div>
                  <Label className="text-gray-300">Invite Participants</Label>
                  <ScrollArea className="h-32 border border-gray-700 rounded-md p-2">
                    {users.map(user => (
                      <div 
                        key={user.id}
                        onClick={() => {
                          setNewMeeting(prev => ({
                            ...prev,
                            participants: prev.participants.includes(user.id)
                              ? prev.participants.filter(id => id !== user.id)
                              : [...prev.participants, user.id]
                          }));
                        }}
                        className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${
                          newMeeting.participants.includes(user.id)
                            ? 'bg-[#C9A961]/20 border border-[#C9A961]'
                            : 'hover:bg-gray-800'
                        }`}
                      >
                        <div className="w-8 h-8 rounded-full flex items-center justify-center text-black font-bold" style={{ backgroundColor: BRONZE }}>
                          {user.first_name?.charAt(0) || user.username?.charAt(0)}
                        </div>
                        <span className="text-white text-sm">{user.first_name} {user.last_name}</span>
                      </div>
                    ))}
                  </ScrollArea>
                </div>
                
                <Button 
                  onClick={handleCreateMeeting}
                  className="w-full"
                  style={{ backgroundColor: BRONZE, color: 'black' }}
                >
                  Schedule Meeting
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-[#C9A961]/20 to-black border-[#C9A961]/30 cursor-pointer hover:border-[#C9A961] transition-all" onClick={handleInstantMeeting}>
          <CardContent className="p-6 flex items-center gap-4">
            <div className="p-3 rounded-lg" style={{ backgroundColor: `${BRONZE}20` }}>
              <Video className="h-8 w-8" style={{ color: BRONZE }} />
            </div>
            <div>
              <h3 className="font-semibold text-white text-lg">New Meeting</h3>
              <p className="text-gray-400 text-sm">Start an instant video call</p>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-[#C9A961]/10 to-black border-[#C9A961]/20 cursor-pointer hover:border-[#C9A961] transition-all" onClick={() => setShowCreateDialog(true)}>
          <CardContent className="p-6 flex items-center gap-4">
            <div className="p-3 rounded-lg" style={{ backgroundColor: `${BRONZE}20` }}>
              <Calendar className="h-8 w-8" style={{ color: BRONZE }} />
            </div>
            <div>
              <h3 className="font-semibold text-white text-lg">Schedule</h3>
              <p className="text-gray-400 text-sm">Plan a meeting for later</p>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-[#C9A961]/10 to-black border-[#C9A961]/20 cursor-pointer hover:border-[#C9A961] transition-all">
          <CardContent className="p-6 flex items-center gap-4">
            <div className="p-3 rounded-lg" style={{ backgroundColor: `${BRONZE}20` }}>
              <Share2 className="h-8 w-8" style={{ color: BRONZE }} />
            </div>
            <div>
              <h3 className="font-semibold text-white text-lg">Share Screen</h3>
              <p className="text-gray-400 text-sm">Present to your team</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Meetings Tabs */}
      <Tabs defaultValue="upcoming" className="w-full">
        <TabsList className="bg-black/50 border border-gray-700">
          <TabsTrigger 
            value="upcoming" 
            className="data-[state=active]:bg-[#C9A961] data-[state=active]:text-black"
          >
            Upcoming ({upcomingMeetings.length})
          </TabsTrigger>
          <TabsTrigger 
            value="past"
            className="data-[state=active]:bg-[#C9A961] data-[state=active]:text-black"
          >
            Past Meetings ({pastMeetings.length})
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="upcoming" className="mt-4">
          {loading ? (
            <div className="flex justify-center p-8">
              <div className="animate-spin h-8 w-8 border-2 border-t-transparent rounded-full" style={{ borderColor: BRONZE }}></div>
            </div>
          ) : upcomingMeetings.length === 0 ? (
            <Card className="bg-black/30 border-[#C9A961]/20">
              <CardContent className="p-8 text-center">
                <Calendar className="h-12 w-12 mx-auto mb-4" style={{ color: BRONZE }} />
                <h3 className="text-xl font-semibold text-white mb-2">No Upcoming Meetings</h3>
                <p className="text-gray-400 mb-4">Schedule a meeting or start an instant call</p>
                <Button onClick={() => setShowCreateDialog(true)} style={{ backgroundColor: BRONZE }} className="text-black">
                  <Plus className="h-4 w-4 mr-2" />
                  Schedule Meeting
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {upcomingMeetings.map(meeting => (
                <MeetingCard
                  key={meeting.id}
                  meeting={meeting}
                  onJoin={handleJoinMeeting}
                  onDelete={handleDeleteMeeting}
                  onCopyLink={handleCopyLink}
                />
              ))}
            </div>
          )}
        </TabsContent>
        
        <TabsContent value="past" className="mt-4">
          {pastMeetings.length === 0 ? (
            <Card className="bg-black/30 border-[#C9A961]/20">
              <CardContent className="p-8 text-center">
                <Clock className="h-12 w-12 mx-auto mb-4 text-gray-500" />
                <h3 className="text-xl font-semibold text-white mb-2">No Past Meetings</h3>
                <p className="text-gray-400">Your meeting history will appear here</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {pastMeetings.map(meeting => (
                <MeetingCard
                  key={meeting.id}
                  meeting={meeting}
                  onJoin={handleJoinMeeting}
                  onDelete={handleDeleteMeeting}
                  onCopyLink={handleCopyLink}
                />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default MeetingsPage;
